// src/auth/code-to-password-flow.tsx
//
// The shared "verify access code → set a new password" state machine behind BOTH
// recovery entry points, so the two screens don't each re-implement it:
//   - reset-via-access-code (Flutter reset_password_access_code → reset_password_new_password):
//     the code was just emailed by ForgotPassword; email is editable; resend is offered.
//   - create-password / post-invite (Flutter create_password): the code arrives in
//     the invite link; email is fixed; no resend.
//
// Both are the same two API steps: LoginByCode (verify code → passwordResetToken)
// then SetPassword. Only the copy + which fields are editable differ, so those
// are props. Composes DS TextField/Button/Banner atoms — no raw hex.
import { useState, type FormEvent, type ReactNode } from 'react';
import { Button, TextField, Banner } from '@scripsteam/scrips-signal-ds';
import { AuthScreenShell, AuthLinkButton } from './auth-screen-shell';
import { NewPasswordFields } from './new-password-fields';
import { evaluatePassword } from './password-policy';
import { isBlank } from './email-validation';
import { useForgotPassword, useLoginByCode, useSetPassword } from './use-recovery';

interface CodeToPasswordFlowProps {
  heading: string;
  intro: ReactNode;
  initialEmail: string;
  initialCode?: string;
  /** Reset flow: email editable. Invite flow: email fixed from the link. */
  emailEditable: boolean;
  /** Reset flow offers "Resend code" (re-hits ForgotPassword). Invite: false. */
  showResend: boolean;
  /** CTA label for the password step ("Reset password" / "Create password"). */
  submitLabel: string;
  /** Shown on the sign-in screen after success. */
  onDone: (successMessage: string) => void;
  successMessage: string;
}

type Step = 'code' | 'password';

export function CodeToPasswordFlow({
  heading,
  intro,
  initialEmail,
  initialCode = '',
  emailEditable,
  showResend,
  submitLabel,
  onDone,
  successMessage,
}: CodeToPasswordFlowProps) {
  const [step, setStep] = useState<Step>('code');
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState(initialCode);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetToken, setResetToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resendNote, setResendNote] = useState<string | null>(null);

  const loginByCode = useLoginByCode();
  const setPasswordMut = useSetPassword();
  const forgotPassword = useForgotPassword();

  const evaluation = evaluatePassword(password, confirmPassword);

  async function onVerifyCode(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setResendNote(null);
    try {
      const result = await loginByCode.mutateAsync({ email, code });
      if (!result.passwordResetToken) {
        // Wire contract: a verified code returns the one-shot reset token. A
        // null token means the code/email pair was rejected.
        setError('That code is invalid or has expired. Check the code and try again.');
        return;
      }
      setResetToken(result.passwordResetToken);
      setStep('password');
    } catch {
      setError('That code is invalid or has expired. Check the code and try again.');
    }
  }

  async function onSetPassword(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!resetToken || !evaluation.canSubmit) return;
    try {
      await setPasswordMut.mutateAsync({ email, password, passwordResetToken: resetToken });
      onDone(successMessage);
    } catch {
      setError('We could not set your password. The reset link may have expired — request a new code.');
    }
  }

  async function onResend() {
    setError(null);
    setResendNote(null);
    // Account-enumeration guard — SAME pattern as forgot-password-page's onSubmit:
    // fire ForgotPassword but never branch the UI on its outcome. Unknown emails
    // and invited-but-passwordless accounts return a non-2xx here exactly like
    // they do from the initial forgot-password request; showing a success alert
    // for known accounts and an error alert for everyone else re-opens the same
    // account-enumeration oracle that fix removed. Always show the identical
    // neutral message regardless of what the request actually did.
    try {
      await forgotPassword.mutateAsync({ email });
    } catch {
      // Swallowed on purpose — the outcome must not be observable.
    }
    setResendNote('If an account exists for that email, a new code is on its way.');
  }

  if (step === 'password') {
    return (
      <AuthScreenShell heading={heading} intro="Choose a new password for your account.">
        <form
          onSubmit={onSetPassword}
          className="flex flex-col gap-4"
        >
          {error && <Banner variant="error" role="alert">{error}</Banner>}
          <NewPasswordFields
            password={password}
            confirmPassword={confirmPassword}
            onPasswordChange={setPassword}
            onConfirmChange={setConfirmPassword}
            disabled={setPasswordMut.isPending}
          />
          <Button
            type="submit"
            variant="primary"
            loading={setPasswordMut.isPending}
            disabled={!evaluation.canSubmit || setPasswordMut.isPending}
          >
            {submitLabel}
          </Button>
        </form>
      </AuthScreenShell>
    );
  }

  const canVerify = !isBlank(email) && !isBlank(code);
  // Serialize verify vs. resend: both mutations write to the SAME server-side
  // reset code (LoginByCode consumes it, ForgotPassword/resend rotates it), so
  // letting one submit while the other is in flight lets a user race a resend
  // against an in-progress verification — the code the verify request is
  // checking can be rotated out from under it mid-request. Gate every control on
  // BOTH mutations' pending state, not just its own.
  const anyPending = loginByCode.isPending || forgotPassword.isPending;
  return (
    <AuthScreenShell heading={heading} intro={intro}>
      <form
        onSubmit={onVerifyCode}
        className="flex flex-col gap-4"
      >
        {error && <Banner variant="error" role="alert">{error}</Banner>}
        {resendNote && <Banner variant="success" role="status">{resendNote}</Banner>}
        {/* No type="email" — see forgot-password-page (HTML5 email validation
            is forbidden here; it intercepts submit + is more permissive). */}
        <TextField
          label="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="username"
          readOnly={!emailEditable}
          disabled={anyPending}
        />
        <TextField
          label="Access code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          autoComplete="one-time-code"
          disabled={anyPending}
        />
        <Button
          type="submit"
          variant="primary"
          loading={loginByCode.isPending}
          disabled={!canVerify || anyPending}
        >
          Verify code
        </Button>
        {showResend && (
          <div className="flex justify-center">
            <AuthLinkButton onClick={onResend} disabled={anyPending || isBlank(email)}>
              Resend code
            </AuthLinkButton>
          </div>
        )}
      </form>
    </AuthScreenShell>
  );
}
